import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { Contact } from 'src/model/contact.model';
import { DuitLebih, IAPIDuitLebih } from 'src/model/duitLebih.model';
import { FileInterface } from 'src/model/file.interface';
import { IAPILoan, Loan } from 'src/model/loan.model';
import { IAPISettlementCharges, SettlementCharges } from 'src/model/settlementCharges.model';
import { FileAdapter, FileUrlBuilder } from './file.service';

export interface GetProfileLoansOptions {
  status?: number[],
  schemes?: string[],
  withLegacyEPF?: boolean,
  includedSharedProfile?: boolean
}

type GetCustLoansOptions = GetProfileLoansOptions & {
  sharedProfileId?: number,
}

type GetCompLoansOptions = GetCustLoansOptions & {
  customerId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class LoanAdapter {
  adapt(item): Loan {
    return new Loan(
      item.id,
      item.profileId,
      item.principal,
      item.stamp ?? 0,
      item.receiptNumber,
      item.status,
      new Date(item.dateCreated),
      item.companyId,
      item.companyCode,
      item.scheme,
      item.interestRate,
      item.term,
      item.customerId,
      item.customerName,
      item.icNumber,
      item.extraInterest,
      item.issuer,
      item.loanCode,
      item.arrearCount,
      item.principalPaid,
      item.repayment,
      item.reserve ?? 0,
      item.balance,
      item.monthlyPayment,
      item.arrearPaid,
      item.serviceCharge ?? 0,
      (item.extras && item.extras.map(p => this.adaptExtras(p))),
      item.isPendingCommission,
      item.monthlyInterest,
      item.discount,
      item.processingRate,
      item.processingCharge,
      item.isSelected ?? false,
      item.epf,
      item.settlementStamp ?? 0,
      item.settlementServiceCharge ?? 0,
      item.settlementExtras ?? 0,
      item.repaymentPaid,
      item.pnl,
      item.principalOut,
      item.termLeft,
      item.legacySettlement,
      item.loanDocExisted ?? false,
      ((item.settlementCharges || item.settlementInfo) && this.adaptSC(item)),
      item.sharedCompanies,
      item.mainCompanyId,
      item.mainCustomerId,
      item.mainCompanyCode,
      item.companyCode,
      item.earlySettlementAmount,
      item.loanGroupId,
      (item.settlementCharges && Object.keys(item.settlementCharges).length > 0 ) ?? false
    );
  }

  adaptExtras(item: any): DuitLebih {
    return new DuitLebih(item.amount, item.subtype);
  }

  adaptContact(item: any): Contact {
    return new Contact(
      item.id,
      item.name,
      item.icNumber,
      item.number,
      undefined,
      undefined,
      item.relation,
      item.remark
    );
  }

  adaptSC(loanItem: any): SettlementCharges {
    let result: SettlementCharges;
    const item = loanItem.settlementInfo ?? loanItem.settlementCharges;
    if (Object.keys(item).length > 0) {
      result = new SettlementCharges(
        item.serviceCharge ?? 0,
        item.stamp ?? 0,
        item.extras ?? 0,
        item.handlingCharge
      )
    } else {
      result = new SettlementCharges(
        loanItem.settlementServiceCharge ?? 0,
        loanItem.settlementStamp ?? 0,
        loanItem.settlementExtras ?? 0,
        loanItem.handlingCharge
      );
    }
    return result;
  }

  toAPIModelSC(settlementCharges: SettlementCharges): IAPISettlementCharges {
    return {
      serviceCharge: settlementCharges.ServiceCharge,
      stamp: settlementCharges.Stamp,
      ...(settlementCharges.Extras && { extras: settlementCharges.Extras }),
      ...(settlementCharges.HandlingCharge && { handlingCharge: settlementCharges.HandlingCharge })
    }
  }

  toAPIModelExtras(extras: DuitLebih): IAPIDuitLebih {
    return {
      amount: extras.Amount,
      ...(extras.Subtype && { subtype: extras.Subtype })
    }
  }

  toAPIModel(loan: Loan): IAPILoan {
    return {
      ...(loan.ProfileId && { profileId: loan.ProfileId }),
      principal: loan.Principal,
      dateCreated: loan.DateCreated.toISOString(),
      scheme: loan.Scheme,
      interestRate: loan.InterestRate,
      term: loan.Term,
      reserve: loan.Reserve,
      stamp: loan.StampAmount,
      serviceCharge: loan.ServiceCharge,
      extras: loan.Extras.map(e => this.toAPIModelExtras(e)),
      processingRate: loan.ProcessingRate,
      settlementCharges: this.toAPIModelSC(loan.SettlementCharges)
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class LoanService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(LoanAdapter);
  private fileAdapter = inject(FileAdapter);
  private fileUrl = inject(FileUrlBuilder);

  getLoanById(id: number, inclShared?: boolean): Promise<Loan> {
    return new Promise<Loan>((resolve, reject) => {
      this.api.get<Loan[]>(this.constructApi.getAllLoans([id], inclShared)).pipe(
        map(data => {
          if (data?.length > 0)
            return this.adapter.adapt(data[0])
          else
            throw new Error(`Loan (id: ${id}) not found`);
        })
      ).subscribe({
        next: response => resolve(response),
        error: err => {
            this.api.handleError(err);
            console.error(err);
          reject(err);
        }
      });
    });
  }

  getLoans(ids?: number[]): Promise<Loan[]> {
    return new Promise<Loan[]>((resolve, reject) => {
      this.api.get<Loan[]>(this.constructApi.getAllLoans(ids)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          console.error(err);
          this.api.handleError(err);
          reject(err);
        }
      });
    });
  }

  getCustomerLoans(custId: number, o?: GetCustLoansOptions): Promise<Loan[]> {
    return new Promise<Loan[]>((resolve, reject) => {
      this.api.get<Loan[]>(this.constructApi.getLoanOfCustomer(custId, o?.status, o?.schemes, o?.withLegacyEPF, o?.sharedProfileId, o?.includedSharedProfile)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          console.error(err);
          this.api.handleError(err);
          reject(err);
        }
      });
    });
  }

  getLoanOfCustomerProfile(profileID: number, o?: GetProfileLoansOptions): Promise<Loan[]> {
    return new Promise<Loan[]>((resolve, reject) => {
      this.api.get<Loan[]>(this.constructApi.getLoanOfCustomerProfile(profileID, o?.status, o?.schemes, o?.withLegacyEPF, o?.includedSharedProfile)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          console.error(err);
          this.api.handleError(err);
          reject(err);
        }
      });
    });
  }

  getCompanyLoans(compId: number, o?: GetCompLoansOptions): Promise<Loan[]> {
    return new Promise<Loan[]>((resolve, reject) => {
      this.api.get<Loan[]>(this.constructApi.getCompanyLoans(compId, o?.status, o?.schemes, o?.withLegacyEPF, o?.sharedProfileId, o?.includedSharedProfile, o?.customerId)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          console.error(err);
          this.api.handleError(err);
          reject(err);
        }
      });
    });
  }

  getCustomerLoanAllFiles(customerId: number, loanId: number): Promise<FileInterface[]> {
    return new Promise<FileInterface[]>((resolve, reject) => {
      this.api.get<FileInterface[]>(this.constructApi.getCustomerLoanAllFiles(customerId, loanId)).pipe(
        map(data => data.map(d => this.fileAdapter.adapt(d, this.fileUrl.buildCustomerFileUrl(customerId))))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          console.error(err);
          this.api.handleError(err);
          reject(err);
        }
      });
    });
  }

  getCustomerLoansWithFiles(customerId: number): Promise<number[]>{
    return new Promise<number[]>((resolve, reject) => {
      this.api.get<{ids: number[]}>(this.constructApi.getCustomerLoansWithFiles(customerId)).subscribe({
        next: response => resolve(response.ids),
        error: err => {
          console.error(err);
          this.api.handleError(err);
          reject(err);
        }
      });
    });
  }

  getAllPendingCommLoanIds(profileId: number): Promise<number[]> {
    return new Promise<number[]>((resolve, reject) => {
      this.api.get<any[]>(this.constructApi.getAllPendingCommissions(profileId)).pipe(
        map(data => data.map(d => d.loanId))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          console.error(err);
          this.api.handleError(err);
          reject(err);
        }
      });
    });
  }
}
